import type { CatalogItemData, QuoteEditableFields, QuoteItem, QuoteView, TaxMode } from "@contracts/index";
import { computeTotals } from "@domain/quote";
import { suggestTaxMode } from "@domain/tax";
import { centsToInput, parseMoneyToCents } from "./format";

/**
 * Form model for the quote editor. Inputs are kept as strings while typing and
 * converted to the API shape (cents, numbers) only on save or for previews.
 */
export interface ItemForm {
  id: string;
  concept: string;
  description: string;
  quantity: string;
  unitPrice: string;
  optional: boolean;
  selected: boolean;
}

export interface ContactForm {
  name: string;
  email: string;
  role: string;
}

export interface QuoteForm {
  language: "es" | "en";
  currency: string;
  clientName: string;
  clientCountry: string;
  clientTaxId: string;
  taxMode: TaxMode;
  /** True once the admin picks the tax mode by hand; stops auto-suggestion by country. */
  taxModeTouched: boolean;
  paymentMethod: "shopify" | "transfer";
  validUntil: string;
  title: string;
  intro: string;
  terms: string;
  contacts: ContactForm[];
  items: ItemForm[];
}

export function newItemId(): string {
  return crypto.randomUUID();
}

export function emptyItem(): ItemForm {
  return { id: newItemId(), concept: "", description: "", quantity: "1", unitPrice: "", optional: false, selected: true };
}

export function emptyForm(validUntil: string): QuoteForm {
  return {
    language: "es",
    currency: "MXN",
    clientName: "",
    clientCountry: "MX",
    clientTaxId: "",
    taxMode: "iva16",
    taxModeTouched: false,
    paymentMethod: "shopify",
    validUntil,
    title: "",
    intro: "",
    terms: "",
    contacts: [{ name: "", email: "", role: "" }],
    items: [emptyItem()],
  };
}

export function formFromQuote(quote: QuoteView): QuoteForm {
  return {
    language: quote.language,
    currency: quote.currency,
    clientName: quote.client.name,
    clientCountry: quote.client.country,
    clientTaxId: quote.client.taxId ?? "",
    taxMode: quote.taxMode,
    taxModeTouched: true,
    paymentMethod: quote.paymentMethod,
    validUntil: quote.validUntil,
    title: quote.title,
    intro: quote.intro,
    terms: quote.terms,
    contacts: quote.contacts.map((c) => ({ name: c.name, email: c.email, role: c.role ?? "" })),
    items: quote.items.map((item) => ({
      id: item.id,
      concept: item.concept,
      description: item.description,
      quantity: String(item.quantity),
      unitPrice: centsToInput(item.unitPriceCents),
      optional: item.optional,
      selected: item.selected,
    })),
  };
}

/** Applies a country change, re-suggesting the tax mode unless the admin chose it. */
export function withCountry(form: QuoteForm, country: string): QuoteForm {
  const clientCountry = country.toUpperCase();
  return {
    ...form,
    clientCountry,
    taxMode: form.taxModeTouched ? form.taxMode : suggestTaxMode(clientCountry),
  };
}

export function itemFromCatalog(entry: CatalogItemData, currency: string): ItemForm {
  const price = entry.prices[currency];
  return {
    id: newItemId(),
    concept: entry.concept,
    description: entry.description,
    quantity: "1",
    unitPrice: price === undefined ? "" : centsToInput(price),
    optional: false,
    selected: true,
  };
}

function toItem(item: ItemForm): QuoteItem {
  return {
    id: item.id,
    concept: item.concept,
    description: item.description,
    quantity: Number(item.quantity),
    unitPriceCents: parseMoneyToCents(item.unitPrice) ?? Number.NaN,
    optional: item.optional,
    selected: item.optional ? item.selected : true,
  };
}

/** Converts the form to the API payload. Server-side validation is the source of truth. */
export function toEditableFields(form: QuoteForm): QuoteEditableFields {
  const contacts = form.contacts
    .filter((c) => c.name.trim() || c.email.trim())
    .map((c) => ({ name: c.name, email: c.email, ...(c.role.trim() ? { role: c.role } : {}) }));
  return {
    language: form.language,
    currency: form.currency.trim().toUpperCase(),
    client: {
      name: form.clientName,
      country: form.clientCountry,
      ...(form.clientTaxId.trim() ? { taxId: form.clientTaxId } : {}),
    },
    contacts,
    title: form.title,
    intro: form.intro,
    terms: form.terms,
    items: form.items.map(toItem),
    taxMode: form.taxMode,
    paymentMethod: form.paymentMethod,
    validUntil: form.validUntil,
  };
}

/** Live totals while typing; invalid rows count as zero. */
export function previewTotals(form: QuoteForm) {
  const items = form.items.map((item) => {
    const quantity = Number(item.quantity);
    const unitPriceCents = parseMoneyToCents(item.unitPrice);
    return {
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 0,
      unitPriceCents: unitPriceCents ?? 0,
      optional: item.optional,
      selected: item.selected,
    };
  });
  return computeTotals(items, form.taxMode, form.currency.trim().toUpperCase() || "MXN");
}
